import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { User, Lock, Eye, EyeOff, ShieldCheck, ArrowRight, ArrowLeft, Building2 } from "lucide-react";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../api/config";

const UserLogin = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [branchInfo, setBranchInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const selectedBranch = JSON.parse(sessionStorage.getItem("selectedBranch") || "{}");
      if (!selectedBranch.branchId) {
        navigate("/branchSelection");
        return;
      }
      setBranchInfo(selectedBranch);
    } catch (err) {
      console.error("Error parsing selectedBranch:", err);
      navigate("/branchSelection");
    }
  }, [navigate]);

  const validateForm = () => {
    const formErrors = {};
    if (!userId.trim()) {
      formErrors.userId = "Branch User ID is required";
    }
    if (!password.trim()) {
      formErrors.password = "Password is required";
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || !branchInfo) return;

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API_BASE_URL}/user/login-user`, {
        userId,
        password,
        branchId: branchInfo.branchId,
      });

      if (response.data && response.data.token) {
        sessionStorage.setItem(
          "user",
          JSON.stringify({
            userId: response.data.userId,
            name: response.data.name,
            password: password,
            role: response.data.role,
            token: response.data.token,
            branchId: response.data.branchId,
            branchName: branchInfo.branchName,
          })
        );

        setSuccessMessage(`Welcome ${response.data.name}! Login successful.`);
        window.location.href = "/";
      }
    } catch (error) {
      console.error("User login failed:", error);
      setErrorMessage(error?.response?.data?.message || "❌ Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToBranches = () => {
    navigate("/branchSelection");
  };

  if (!branchInfo) {
    return (
      <div className="min-h-screen bg-[#f8fafc] text-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-[#77c8c7] border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">Loading branch information...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] via-[#f1f5f9] to-[#eff6ff] text-slate-900 flex flex-col justify-between relative overflow-hidden font-sans">
      <ResponsiveAppBar />

      {/* Glowing Ambient Background Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#936097]/10 rounded-full filter blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#77c8c7]/15 rounded-full filter blur-[140px] pointer-events-none" />

      {/* Main Login Form Container */}
      <div className="container mx-auto px-6 py-12 flex items-center justify-center relative z-10 flex-1">
        <div className="w-full max-w-md">
          {/* Glass Card */}
          <div className="bg-white/95 backdrop-blur-2xl border border-slate-200/80 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-slate-300/40 relative overflow-hidden">
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#936097] via-[#77c8c7] to-[#936097]" />

            {/* Logo & Header */}
            <div className="text-center mb-8">
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-slate-50 to-teal-50/40 border border-slate-200/60 mb-4 shadow-sm">
                <img
                  src="/mainlogo.png"
                  alt="Velavan Finance"
                  className="h-14 w-auto object-contain drop-shadow-sm"
                />
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Branch Login
              </h1>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 border border-teal-200/60 text-xs font-semibold text-[#0d9488] mt-2">
                <Building2 className="w-3.5 h-3.5" />
                <span>{branchInfo.branchName}</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* User ID Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider block">
                  Branch User ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-[#0d9488]">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    placeholder="Enter your Branch User ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    disabled={isLoading}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-[#77c8c7] focus:ring-2 focus:ring-[#77c8c7]/30 transition-all text-sm font-medium"
                  />
                </div>
                {errors.userId && (
                  <span className="text-xs text-rose-500 font-medium pl-1">{errors.userId}</span>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider block">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-[#936097]">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-[#936097] focus:ring-2 focus:ring-[#936097]/30 transition-all text-sm font-medium"
                  />
                  <button
                    type="button"
                    onClick={handleTogglePassword}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5 text-[#0d9488]" />}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-xs text-rose-500 font-medium pl-1">{errors.password}</span>
                )}
              </div>

              {/* Success / Error Messages */}
              {successMessage && (
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
                  {successMessage}
                </div>
              )}
              {errorMessage && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
                  {errorMessage}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#936097] via-[#864e8b] to-[#77c8c7] hover:opacity-95 shadow-lg shadow-[#936097]/25 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer text-sm uppercase tracking-wider"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <>
                    <span>Authenticate & Enter</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Back to Branches */}
              <button
                type="button"
                onClick={handleBackToBranches}
                disabled={isLoading}
                className="w-full py-2.5 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Switch Branch Location</span>
              </button>
            </form>

            {/* Security Guarantee Badge */}
            <div className="mt-8 pt-6 border-t border-slate-100 text-center flex items-center justify-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-[#0d9488]" />
              <span>Encrypted Session • Role Authorized Access</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UserLogin;